import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CreditCard,
  Wallet,
  RotateCcw,
  Save,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { payrollService } from '@/services/payroll.service';

interface LAConfigState {
  max_loan_amount: number;
  max_advance_amount: number;
  default_interest_rate: number;
  max_loan_emis: number;
  max_active_loans: number;
  require_loan_reason: boolean;
  require_advance_reason: boolean;
}

const DEFAULTS: LAConfigState = {
  max_loan_amount: 500000,
  max_advance_amount: 100000,
  default_interest_rate: 0,
  max_loan_emis: 24,
  max_active_loans: 1,
  require_loan_reason: false,
  require_advance_reason: false,
};

function formatINR(val: number): string {
  return val.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function LoansAdvancesConfigSection() {
  const queryClient = useQueryClient();
  const [local, setLocal] = useState<LAConfigState>(DEFAULTS);

  const { data: config, isLoading } = useQuery({
    queryKey: ['payroll', 'la-config'],
    queryFn: () => payrollService.getLAConfig(),
  });

  useEffect(() => {
    if (config) {
      setLocal({
        max_loan_amount:       parseFloat(config.max_loan_amount),
        max_advance_amount:    parseFloat(config.max_advance_amount),
        default_interest_rate: parseFloat(config.default_interest_rate),
        max_loan_emis:         config.max_loan_emis,
        max_active_loans:      config.max_active_loans,
        require_loan_reason:   config.require_loan_reason,
        require_advance_reason: config.require_advance_reason,
      });
    }
  }, [config]);

  const save = useMutation({
    mutationFn: () =>
      payrollService.updateLAConfig({
        max_loan_amount:       local.max_loan_amount,
        max_advance_amount:    local.max_advance_amount,
        default_interest_rate: local.default_interest_rate,
        max_loan_emis:         local.max_loan_emis,
        max_active_loans:      local.max_active_loans,
        require_loan_reason:   local.require_loan_reason,
        require_advance_reason: local.require_advance_reason,
      }),
    onSuccess: () => {
      toast.success('Loans & Advances config saved');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'la-config'] });
    },
    onError: () => toast.error('Failed to save config'),
  });

  const set = <K extends keyof LAConfigState>(key: K, value: LAConfigState[K]) =>
    setLocal((prev) => ({ ...prev, [key]: value }));

  const reset = () => {
    if (config) {
      setLocal({
        max_loan_amount:       parseFloat(config.max_loan_amount),
        max_advance_amount:    parseFloat(config.max_advance_amount),
        default_interest_rate: parseFloat(config.default_interest_rate),
        max_loan_emis:         config.max_loan_emis,
        max_active_loans:      config.max_active_loans,
        require_loan_reason:   config.require_loan_reason,
        require_advance_reason: config.require_advance_reason,
      });
    }
  };

  const isDirty = config
    ? JSON.stringify(local) !== JSON.stringify({
        max_loan_amount:       parseFloat(config.max_loan_amount),
        max_advance_amount:    parseFloat(config.max_advance_amount),
        default_interest_rate: parseFloat(config.default_interest_rate),
        max_loan_emis:         config.max_loan_emis,
        max_active_loans:      config.max_active_loans,
        require_loan_reason:   config.require_loan_reason,
        require_advance_reason: config.require_advance_reason,
      })
    : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading config…
      </div>
    );
  }

  const emiAmount =
    local.max_loan_amount > 0 && local.max_loan_emis > 0
      ? local.max_loan_amount / local.max_loan_emis
      : 0;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-4 py-3 text-sm">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-foreground">
          Employees can borrow up to{' '}
          <strong>₹{formatINR(local.max_loan_amount)}</strong> across up to{' '}
          <strong>{local.max_active_loans}</strong> active loan{local.max_active_loans !== 1 ? 's' : ''}, repaid in up to{' '}
          <strong>{local.max_loan_emis} EMIs</strong> (≈ ₹{formatINR(Math.ceil(emiAmount))}/mo at max).
          Salary advances are capped at{' '}
          <strong>₹{formatINR(local.max_advance_amount)}</strong> per request.
        </p>
      </div>

      {/* Card 1 — Loan Rules */}
      <div className="rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
          <CreditCard className="h-4 w-4 text-teal-600" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">Loan Rules</h3>
            <p className="text-xs text-muted-foreground">
              Limits applied when creating employee loans. EMI is deducted from monthly salary.
            </p>
          </div>
        </div>

        <div className="divide-y divide-border/40">
          <SettingRow
            title="Maximum Loan Amount"
            desc="Highest principal an employee can borrow in a single loan."
          >
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-muted-foreground">₹</span>
              <Input
                type="number"
                min={1000}
                max={10000000}
                step={1000}
                value={local.max_loan_amount}
                onChange={(e) => set('max_loan_amount', Math.max(1000, parseInt(e.target.value) || 1000))}
                className="h-9 w-32 text-right"
              />
            </div>
          </SettingRow>

          <SettingRow
            title="Maximum EMIs"
            desc="Maximum number of monthly instalments allowed for repayment."
          >
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={120}
                value={local.max_loan_emis}
                onChange={(e) => set('max_loan_emis', Math.min(120, Math.max(1, parseInt(e.target.value) || 1)))}
                className="h-9 w-24 text-right"
              />
              <span className="text-xs text-muted-foreground">months</span>
            </div>
          </SettingRow>

          <SettingRow
            title="Default Interest Rate"
            desc="Pre-filled rate when creating a new loan. Can be overridden per loan."
          >
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={local.default_interest_rate}
                onChange={(e) =>
                  set('default_interest_rate', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))
                }
                className="h-9 w-24 text-right"
              />
              <span className="text-sm text-muted-foreground">% p.a.</span>
            </div>
          </SettingRow>

          <SettingRow
            title="Max Active Loans per Employee"
            desc="How many concurrent active loans an employee can hold at once."
          >
            <Input
              type="number"
              min={1}
              max={10}
              value={local.max_active_loans}
              onChange={(e) => set('max_active_loans', Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
              className="h-9 w-24 text-right"
            />
          </SettingRow>

          <ToggleRow
            title="Require Reason for Loans"
            desc="When enabled, the reason field becomes mandatory when creating a loan."
            checked={local.require_loan_reason}
            onChange={(v) => set('require_loan_reason', v)}
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/40 px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            disabled={!isDirty || save.isPending}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reset
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending}
          >
            {save.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Card 2 — Advance Rules */}
      <div className="rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border/40 px-4 py-3">
          <Wallet className="h-4 w-4 text-amber-600" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">Salary Advance Rules</h3>
            <p className="text-xs text-muted-foreground">
              Limits applied when creating salary advances. Deducted from the specified month's salary.
            </p>
          </div>
        </div>

        <div className="divide-y divide-border/40">
          <SettingRow
            title="Maximum Advance Amount"
            desc="Highest amount an employee can receive as a salary advance."
          >
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-muted-foreground">₹</span>
              <Input
                type="number"
                min={100}
                max={1000000}
                step={500}
                value={local.max_advance_amount}
                onChange={(e) =>
                  set('max_advance_amount', Math.max(100, parseInt(e.target.value) || 100))
                }
                className="h-9 w-32 text-right"
              />
            </div>
          </SettingRow>

          <ToggleRow
            title="Require Reason for Advances"
            desc="When enabled, the reason field becomes mandatory when creating an advance."
            checked={local.require_advance_reason}
            onChange={(v) => set('require_advance_reason', v)}
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/40 px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={reset}
            disabled={!isDirty || save.isPending}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Reset
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending}
          >
            {save.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Shared layout helpers ────────────────────────────────────────────────────

function SettingRow({
  title, desc, children,
}: {
  title: string; desc: string; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      {children}
    </div>
  );
}

function ToggleRow({
  title, desc, checked, onChange,
}: {
  title: string; desc: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer select-none items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <Switch checked={checked} onChange={onChange} size="sm" aria-label={title} />
    </label>
  );
}
